import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import * as apiUtility from "../../config/APIUtils";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorPopUp from "../Utility/ErrorPopUp";

export default function PolicyFormModal({ open, onClose, initialData = {}, onSubmitSuccess, orgId, homeId, roleId }) {
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);

  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [internalMetadata, setInternalMetadata] = useState({
    policyId: null,
    versionNumber: 1,
    archived: false,
    home_id: homeId,
    org_id: orgId,
    role_id: roleId,
    isMajorChange: false,
    status: 'draft'
  });

  const [formData, setFormData] = useState({
    title: "",
    policyNumber: "",
    type: "",
    description: "",
    expiry: 0,
    expiryType: "months",
    reAssignAll: false,
    assignExistingTemplate: false
  });

  const [pdfFile, setPdfFile] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);

  // Initialize/reset form state
  const resetForm = useCallback((data = {}) => {
    //console.log("Resetting form with data:", data);
    setFormData({
      title: data?.title || "",
      policyNumber: data?.policyNumber || "",
      type: data?.type || "Other",
      description: data?.description || "",
      expiry: data?.expiry ?? 0,
      expiryType: data?.expiryType || "months",
      reAssignAll: data?.reAssignAll || false,
      assignExistingTemplate: data?.assignExistingTemplate || false
    });

    setInternalMetadata({
      policyId: data?.policyId || null,
      versionNumber: data?.versionNumber || 1,
      archived: data?.archived || false,
      status: data?.status || 'draft',
      isMajorChange: false,
      org_id: data?.org_id || "",
      home_id: data?.home_id || "",
      role_id: data?.role_id || ""
    });

    if (data?.url) {
      setExistingFileUrl(data.url);
      setPdfPreview(data.url);
    } else {
      setExistingFileUrl(null);
      setPdfPreview(null);
    }
  }, []);

  useEffect(() => {
    //console.log(initialData, orgId, homeId, roleId);
    if (open) {
      resetForm(initialData);
    }
  }, [open]);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file && file.type === "application/pdf") {
      if (pdfPreview && !existingFileUrl) {
        URL.revokeObjectURL(pdfPreview);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      setPdfFile(file);
      setPdfPreview(newPreviewUrl);
      setExistingFileUrl(null);
      setInternalMetadata(prev => ({
        ...prev,
        isMajorChange: true
      }));

    } else {
      setErrorMsg("Please upload a valid PDF file");
      setShowErrorMsg(true);
    }
  }, [pdfPreview, existingFileUrl]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "application/pdf": [".pdf"] },
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "expiry") {
      const numValue = parseInt(value) || 0;
      if (numValue < 0 || numValue > 12) return;
      setFormData((prev) => ({ ...prev, [name]: numValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleRemovePdf = useCallback(() => {
    if (pdfPreview && !existingFileUrl) {
      URL.revokeObjectURL(pdfPreview);
    }
    setPdfFile(null);
    setPdfPreview(null);
    setExistingFileUrl(null);

  }, [pdfPreview, existingFileUrl]);

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    await submitForm(pendingSubmission);
    setPendingSubmission(null);
  };

  const submitForm = async (formDataToSend) => {
    try {
      let endPoint = "policy/addOrUpdate";
      let apiRequest = apiUtility.apiInputRequest;
      apiRequest.endPoint = endPoint;
      apiRequest.data = formDataToSend;
      const res = await apiUtility.sendAPIDataPOST(apiRequest);

      if (res.message === "Updated successfully" || res.status === 200) {
        //console.log("Uploaded url", res.data.url);
        if (onSubmitSuccess) {
          onSubmitSuccess(res.data);
        }
        setShowConfirmDialog(false);
        onClose();
        resetForm();
      }
    } catch (error) {
      console.error(error);
      setErrorMsg("Failed to submit policy");
      setShowErrorMsg(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || formData.title.trim() === "") {
      setErrorMsg("Title is mandatory");
      setShowErrorMsg(true);
      return;
    }

    if (!formData.type || formData.type.trim() === "") {
      setErrorMsg("Type is mandatory");
      setShowErrorMsg(true);
      return;
    }
    if (!formData.policyNumber || formData.policyNumber.trim() === "") {
      setErrorMsg("Policy Version Number is mandatory");
      setShowErrorMsg(true);
      return;
    }
    if (!pdfFile && !pdfPreview) {
      setErrorMsg("Please upload a PDF file");
      setShowErrorMsg(true);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("type", formData.type);
    formDataToSend.append("policyNumber", formData.policyNumber);
    formDataToSend.append("description", formData.description);
    if (pdfFile) {
      formDataToSend.append("pdfFile", pdfFile);
    }
    formDataToSend.append("replacedFileUrl", existingFileUrl || "");
    formDataToSend.append("expiry", formData.expiry);
    formDataToSend.append("expiryType", formData.expiryType);
    formDataToSend.append("reAssignAll", formData.reAssignAll);
    formDataToSend.append("assignExistingTemplate", formData.assignExistingTemplate);



    formDataToSend.append("org_id", orgId);
    formDataToSend.append("home_id", homeId);
    formDataToSend.append("policyId", internalMetadata.policyId);
    formDataToSend.append("versionNumber", internalMetadata.versionNumber);
    formDataToSend.append("archived", internalMetadata.archived);
    formDataToSend.append("status", internalMetadata.status);
    formDataToSend.append("isMajorChange", internalMetadata.isMajorChange);

    if (internalMetadata.isMajorChange && internalMetadata.policyId) {
      setPendingSubmission(formDataToSend);
      setShowConfirmDialog(true);
      return;
    }
    //console.log("formData.assignExistingTemplate",formData.assignExistingTemplate)
    await submitForm(formDataToSend);
  };

  return (
    <>
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Major Update</DialogTitle>
        <DialogContent>
          <Typography>
            This will create a new version of the policy. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle variant="h6">{(internalMetadata?.policyId === null) ? "Add New Home Doc" : "Update Home Doc"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Left side - Form */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2 }} elevation={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Document Details
                </Typography>
                <form onSubmit={handleSubmit}>
                  <TextField
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                  />
                  <TextField
                    label="Version Number"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                  />
                  {/* <TextField
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                /> */}
                  <TextField
                    select
                    label="Type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  >
                    <MenuItem value="Policy Document">Policy Document</MenuItem>
                    <MenuItem value="Training Material">Training Material</MenuItem>
                    <MenuItem value="Procedure Document">Procedure Document</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                  />
                  <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        label="Expiry Duration"
                        name="expiry"
                        type="number"
                        inputProps={{ min: 0, max: 12 }}
                        fullWidth
                        value={formData.expiry}
                        onChange={handleChange}
                        helperText="0 means no expiry"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        select
                        label="Type"
                        name="expiryType"
                        fullWidth
                        value={formData.expiryType}
                        onChange={handleChange}
                        helperText=" "
                      >
                        <MenuItem value="months">Months</MenuItem>
                        <MenuItem value="years">Years</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                  {!internalMetadata.policyId && (
                    <Grid>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.assignExistingTemplate}
                            onChange={(e) => setFormData(prev => ({ ...prev, assignExistingTemplate: e.target.checked }))}
                            name="assignExistingTemplate"
                          />
                        }
                        label="Assign Document to all existing position templates"
                        sx={{ mt: 2 }}
                      />
                    </Grid>
                  )}
                  {internalMetadata.policyId !== null && (
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.reAssignAll}
                            onChange={(e) => setFormData(prev => ({ ...prev, reAssignAll: e.target.checked }))}
                            name="reAssignAll"
                          />
                        }
                        label="Re-assign all staff to this updated policy"
                        sx={{ mt: 2 }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.assignExistingTemplate}
                            onChange={(e) => setFormData(prev => ({ ...prev, assignExistingTemplate: e.target.checked }))}
                            name="assignExistingTemplate"
                          />
                        }
                        label="Assign Document to all existing position templates"
                        sx={{ mt: 2 }}
                      />
                    </div>
                  )}
                </form>
              </Paper>
            </Grid>

            {/* Right side - PDF Upload & Preview */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{ p: 3, borderRadius: 2, textAlign: "center" }}
                elevation={2}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Policy Document (PDF)
                </Typography>

                {!pdfPreview ? (
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                      p: 4,
                      cursor: "pointer",
                      backgroundColor: isDragActive ? "#f5f5f5" : "transparent",
                      transition: "background-color 0.3s",
                    }}
                  >
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <Typography>Drop the PDF here...</Typography>
                    ) : (
                      <Typography>
                        Drag & drop a PDF file here, or click to select
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      {pdfFile ? pdfFile.name : "Existing File"}
                    </Typography>

                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        overflow: "hidden",
                        height: "400px",
                        mt: 1,
                      }}
                    >
                      <iframe
                        src={pdfPreview}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                      ></iframe>
                    </Box>

                    <IconButton
                      color="error"
                      onClick={handleRemovePdf}
                      sx={{ mt: 2 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Typography variant="body2">
                      Remove & re-upload
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.title || (!pdfFile && !pdfPreview)}
          >
            {(internalMetadata?.policyId === null) ? "Submit" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
      <ErrorPopUp show={showErrorMsg} data={errorMsg} onHide={() => { setShowErrorMsg(false) }} />
    </>
  );
}
